import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "../components/SiteHeader";
import { Button } from "../components/ui/button";
import { ArrowLeft, RotateCcw } from "lucide-react";

/**
 * Route: /employers/purchase/cancel?ref=IV-EMP-######
 *
 * Return URL when a buyer clicks "Back" or closes the Stripe hosted
 * Checkout without completing payment. No punitive state — we simply
 * reassure the buyer that nothing was charged and invite them to try
 * again from the /employers landing page.
 */
export default function EmployerPayCancelPage() {
  const [params] = useSearchParams();
  const ref = params.get("ref");
  return (
    <div className="min-h-screen bg-white" data-testid="employer-purchase-cancel-page">
      <SiteHeader />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 text-center">
        <div className="w-14 h-14 mx-auto rounded-full bg-gray-50 flex items-center justify-center mb-6">
          <span className="text-3xl">🕒</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
          Payment not completed
        </h1>
        <p className="text-lg text-gray-600 mb-2 max-w-xl mx-auto leading-relaxed">
          No payment has been taken. You can try again any time from the
          Inter-View for Employers page — nothing is locked in.
        </p>
        {ref && (
          <p className="text-sm text-gray-500 mb-8">
            Your reference for this attempt was{" "}
            <code className="font-mono text-gray-700" data-testid="cancel-service-reference">
              {ref}
            </code>
          </p>
        )}

        <div className="flex flex-wrap gap-3 justify-center">
          <Link to="/employers">
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="cancel-try-again"
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              Try again
            </Button>
          </Link>
          <Link to="/employers/intake">
            <Button
              variant="outline"
              className="border-blue-200 text-blue-700 hover:bg-blue-50"
              data-testid="cancel-inquiry-instead"
            >
              Ask us a question instead
            </Button>
          </Link>
        </div>

        <p className="text-sm text-gray-500 mt-10">
          Need help?{" "}
          <a
            href="mailto:employers@inter-view.ca"
            className="text-blue-700 underline"
          >
            employers@inter-view.ca
          </a>
        </p>

        <div className="mt-8">
          <Link to="/employers" className="text-sm text-gray-500 hover:text-gray-700 inline-flex items-center gap-1">
            <ArrowLeft className="h-3 w-3" />
            Back to Inter-View for Employers
          </Link>
        </div>
      </div>
    </div>
  );
}
